import {Component, ElementRef, Output, ViewChild, EventEmitter, QueryList, ViewChildren} from '@angular/core';
import {
  DeliveryService,
  DROPSHIP_TTN_TOO_LONG,
  dropshipPlacesMismatchText
} from "../../../../services/delivery.service";
import {ModalNpDropshipInfoComponent} from "../../modals/modal-np-dropship-info/modal-np-dropship-info.component";
import {
  MessageDeliveryDropshippingConfirmComponent
} from "../../messages/message-delivery-dropshipping-confirm/message-delivery-dropshipping-confirm.component";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {LocalStorageService} from "../../../../services/local-storage.service";
import {Router} from "@angular/router";
import {takeUntil} from "rxjs/operators";
import {
  DropshipFileKind,
  DropshipFileUploadResponse,
  DropshipLabelParse,
  DropshipPlacesCheck
} from "../../../../interfaces/dropship-file";
import {HTTP_CODES} from "../../../../interfaces/http-codes";

// Нова схема дропшипу (картка #867): ті самі межі, що перевіряє бек (OrderFileService::dropship_upload)
const DROPSHIP_FILE_MAX_SIZE = 5 * 1024 * 1024;
const DROPSHIP_FILE_EXT = /\.(pdf|png|jpe?g)$/i;
const DROPSHIP_FILE_TOO_LARGE = 'Файл завеликий - не більше 5 МБ.';
const DROPSHIP_FILE_WRONG_TYPE = 'Підходять лише файли PDF, PNG або JPG.';
const DROPSHIP_FILE_UPLOAD_FAILED = 'Не вдалося завантажити файл. Спробуйте ще раз.';
// Коди відповіді basket_data/dropship_file_upload - сирий код користувачу не показуємо
const DROPSHIP_UPLOAD_ERRORS: { [code: string]: string } = {
  'dropship new off': 'Завантаження файлів для дропшипу зараз вимкнено. Оновіть сторінку.',
  'file too large': DROPSHIP_FILE_TOO_LARGE,
  'wrong file type': DROPSHIP_FILE_WRONG_TYPE,
  'no file': 'Оберіть файл.',
};
// Номер ТТН вручну (картка #868): як бек (basket_data/pay, dropship_ttn) - лише цифри, не більше 36
const DROPSHIP_TTN_MAX_DIGITS = 36;

interface DropshipFileState {
  id: number;        // id тимчасового файлу на беку, 0 - ще не завантажено
  name: string;
  uploading: boolean;
  uploadingName: string; // файл, що вантажиться зараз, - його ім'я видно поруч із кнопкою до відповіді бека
  error: string;
}

@Component({
  selector: 'app-dropship',
  templateUrl: './dropship.component.html',
  styleUrls: ['../../../basket-confirm/basket-confirm.component.scss']
})
export class DropshipComponent extends DeliveryService {
  @ViewChild('phoneRef') override phoneRef: ElementRef<HTMLInputElement>;
  @ViewChild('scrollInputRef') scrollInputRef: ElementRef<HTMLInputElement>;
  @ViewChild(ModalNpDropshipInfoComponent) modalNpDropshipInfoComponent: ModalNpDropshipInfoComponent;
  @ViewChild(MessageDeliveryDropshippingConfirmComponent) messageDeliveryDropshippingConfirmComponentRef: MessageDeliveryDropshippingConfirmComponent;
  @ViewChildren('dropshipFileInput') dropshipFileInputs: QueryList<ElementRef<HTMLInputElement>>;
  @Output() selectedDeliveryType = new EventEmitter<number>();

  wrongDropshipAddress = false;
  wrongFio = false;
  fio = '';
  private isUserChangedDeliveryDate = false;

  // Наклейка і реєстр (картка #867): вантажаться одразу після вибору, заміна - вибором іншого файлу
  readonly dropshipFileKinds: DropshipFileKind[] = ['label', 'registry'];
  readonly dropshipFileTitles: { [kind in DropshipFileKind]: string } = {label: 'Наклейка', registry: 'Реєстр'};
  dropshipFiles: { [kind in DropshipFileKind]: DropshipFileState } = {
    label: {id: 0, name: '', uploading: false, uploadingName: '', error: ''},
    registry: {id: 0, name: '', uploading: false, uploadingName: '', error: ''},
  };
  // Кнопка «Обрати файл» помітно реагує на клік (картка #899): клас з анімацією, знімається після неї
  dropshipFilePressed: { [kind in DropshipFileKind]: boolean } = {label: false, registry: false};
  // Розпізнане з наклейки (картка #868): номер ТТН і місця, звірка місць з розрахунком
  labelParse: DropshipLabelParse | null = null;
  placesCheck: DropshipPlacesCheck | null = null;
  // Номер ТТН вручну - якщо з наклейки не розпізнався
  manualTtn = '';
  manualTtnError = '';

  constructor(
    http: HttpClient,
    localStorage: LocalStorageService,
    private router: Router
  ) {
    super(http, localStorage);
  }

  override scrollToInvalidForm() {
    if (this.wrongDeliveryDates) {
      this.scrollInputRef.nativeElement.scrollIntoView();
    }
  }

  inputAddress() {
    if (this.lastAddress.city === null || this.lastAddress.city.length < 10) {
      this.wrongDropshipAddress = true;
      this.isValidDeliveryForm = true;
    } else {
      this.wrongDropshipAddress = false;
    }
  }

  showDropshipInstruction() {
    this.modalNpDropshipInfoComponent.show();
  }

  // Бек вмикає нову схему дропшипу налаштуванням dropship_new - тоді форма просить наклейку і реєстр
  get dropshipFilesRequired(): boolean {
    return !!this.deliveryCurrent?.dropship_files_required;
  }

  // Наклейку завантажено, а номер ТТН з неї не розпізнано - просимо вписати вручну
  get labelTtnMissing(): boolean {
    return this.dropshipFilesRequired && !!this.dropshipFiles.label.id && !!this.labelParse && this.labelParse.status !== 'ok';
  }

  // Місця на наклейці не збігаються з розрахунком - замовлення не оформлюємо, доки клієнт не перезавантажить наклейку
  get dropshipPlacesMismatch(): boolean {
    return this.dropshipFilesRequired && !!this.placesCheck && !this.placesCheck.ok;
  }

  get dropshipPlacesMismatchText(): string {
    return this.placesCheck ? dropshipPlacesMismatchText(this.placesCheck.label, this.placesCheck.calc) : '';
  }

  // Номер ТТН вручну - самі цифри, як їх лишить бек
  private manualTtnDigits(): string {
    return (this.manualTtn || '').replace(/\D/g, '');
  }

  inputManualTtn() {
    this.manualTtnError = this.manualTtnDigits().length > DROPSHIP_TTN_MAX_DIGITS ? DROPSHIP_TTN_TOO_LONG : '';
  }

  // Без обох завантажених файлів кнопка «Відправити» заблокована
  get dropshipFilesMissing(): boolean {
    return this.dropshipFilesRequired
      && this.dropshipFileKinds.some(kind => !this.dropshipFiles[kind].id || this.dropshipFiles[kind].uploading);
  }

  // Ім'я поруч із кнопкою вибору (картка #899): що вантажиться зараз, інакше вже завантажене
  dropshipFileName(kind: DropshipFileKind): string {
    const state = this.dropshipFiles[kind];

    return state.uploading ? state.uploadingName : (state.id ? state.name : '');
  }

  onDropshipFileSelected(kind: DropshipFileKind, event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    const state = this.dropshipFiles[kind];

    if (!file) {
      return;
    }

    // Поле одразу очищаємо (картка #899): ім'я файлу показує не браузер, а форма, - а той самий файл
    // після помилки чи перезбереженої наклейки має знову дати change.
    input.value = '';

    // Не підійшов - показуємо причину; уже завантажений файл лишається
    const fail = (text: string) => {
      state.error = text;
    };

    if (!DROPSHIP_FILE_EXT.test(file.name)) {
      fail(DROPSHIP_FILE_WRONG_TYPE);
      return;
    }

    if (file.size > DROPSHIP_FILE_MAX_SIZE) {
      fail(DROPSHIP_FILE_TOO_LARGE);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('kind', kind);

    state.error = '';
    state.uploading = true;
    state.uploadingName = file.name;

    this.http.post<DropshipFileUploadResponse>(this.apiRoot + 'basket_data/dropship_file_upload', formData)
      .pipe(takeUntil(this.isDestroyed))
      .subscribe({
        next: (response) => {
          state.uploading = false;

          if (!response || response.error !== '' || !response.data) {
            fail(DROPSHIP_UPLOAD_ERRORS[response?.error] || DROPSHIP_FILE_UPLOAD_FAILED);
            return;
          }

          state.id = response.data.id;
          state.name = response.data.name || file.name;

          // Нова наклейка - нове розпізнавання і нова звірка місць (картка #868)
          if (kind === 'label') {
            this.labelParse = response.data.parse || {status: 'error', ttn: null, places: null, carrier: null, method: null};
            this.placesCheck = response.data.places_check || null;
          }
        },
        error: (error: HttpErrorResponse) => {
          state.uploading = false;
          // 413 віддає nginx ще до бека
          fail(error && error.status === HTTP_CODES.PAYLOAD_TOO_LARGE ? DROPSHIP_FILE_TOO_LARGE : DROPSHIP_FILE_UPLOAD_FAILED);
        },
        complete: () => {
          // Завершення без відповіді - запит скасовано кнопкою лоадера: файл треба обрати ще раз
          if (state.uploading) {
            state.uploading = false;
            fail('Завантаження скасовано. Оберіть файл ще раз.');
          }
        }
      });
  }

  onUserChangeDeliveryDate() {
    this.isUserChangedDeliveryDate = true;
    this.changingValueDeliveryDate();
  }

  changingValueDeliveryDate() {
    if (this.deliveryDate == -1 && !this.dozakazDate) {
      this.wrongDeliveryDates = true;
      this.isValidDeliveryForm = true;
    } else {
      this.wrongDeliveryDates = false;
    }

    if (this.deliveryDate != -1 && this.deliveryCurrent && this.deliveryCurrent.delivery_hours && this.deliveryCurrent.delivery_hours[this.deliveryDate]) {
      this.deliveryHoursList = this.deliveryCurrent.delivery_hours[this.deliveryDate];
    } else {
      this.deliveryHoursList = [];
    }

    if (this.isUserChangedDeliveryDate) {
      this.deliveryHours = -1;
      this.wrongDeliveryHours = false;
      this.isUserChangedDeliveryDate = false;
    }
  }

  changingValueDeliveryHours() {
    if (!this.deliveryHoursList?.length) {
      this.wrongDeliveryHours = false;
      return;
    }

    if (this.deliveryHours == -1 && !this.dozakazDate) {
      this.wrongDeliveryHours = true;
      this.isValidDeliveryForm = true;
    } else {
      this.wrongDeliveryHours = false;
    }
  }

  inputFio() {
    if (this.fio === '' || this.fio.length < 5) {
      this.wrongFio = true;
      this.isValidDeliveryForm = true;
    } else {
      this.wrongFio = false;
    }
  }

  switchesDeliveryType(id: number) {
    this.selectedDeliveryType.emit(id);
  }

  override validateSendPayment() {
    this.isValidDeliveryForm = false;
    const sendData = {
      delivery_id: this.delivery
    };

    this.inputAddress();
    this.changingValueDeliveryDate();
    this.changingValueDeliveryHours();
    this.inputFio();
    this.inputLastAddressPhone();

    sendData['delivery_date'] = this.deliveryDate;

    sendData['delivery_hour'] = '';

    if (this.deliveryHoursList?.length && this.deliveryHours !== -1) {
      sendData['delivery_hour'] = this.deliveryHours;
    }

    sendData['comment'] = `${this.lastAddress.city}, ${this.fio}, ${this.lastAddress.phone} ${this.deliveryComment}`;

    if (this.dropshipFilesRequired) {
      if (this.dropshipFilesMissing) {
        this.isValidDeliveryForm = true;
      }

      sendData['dropship_label_file_id'] = this.dropshipFiles.label.id;
      sendData['dropship_registry_file_id'] = this.dropshipFiles.registry.id;

      // Картка #868: місця не збігаються - не відправляємо; номер, вписаний вручну, - лише коли не розпізнано
      if (this.dropshipPlacesMismatch) {
        this.isValidDeliveryForm = true;
      }

      if (this.labelTtnMissing) {
        this.inputManualTtn();

        if (this.manualTtnError) {
          this.isValidDeliveryForm = true;
        } else if (this.manualTtnDigits()) {
          sendData['dropship_ttn'] = this.manualTtnDigits();
        }
      }
    }

    // Дати переміщення бек рахує лише по підзамовних товарах: для кошика зі складів 1+7 без них список
    // порожній, а вибір обов'язковий - форма не відправлялась. Без delivery_date_zak бек бере delivery_date.
    if (this.separateOrders && this.deliveryCurrent?.delivery_dates_zak?.length) {
      this.changingValueDeliverySeparate();

      sendData['delivery_date_zak'] = this.deliverySeparate;
    }

    return sendData;
  }

  override resultSendPayment(data) {
    if (data.error === '' && data.data.order_id) {
      data.data.delivery = this.delivery;
      this.isDeliveryConfirmation = true;
      this.messageDeliveryDropshippingConfirmComponentRef.deliveryDropshippingConfirmationShow(data);
    }

    if (data.error === 'empty basket') {
      this.router.navigate(['account']);
    }

    if (data.error === 'too many orders') {
      this.router.navigate(['account']);
    }

    if (data.error === 'not_enough_liters') {
      this.router.navigate(['basket']);
    }

    // Тимчасові файли бек уже не знайшов (зберігає їх добу) - завантажити наново
    if (data.error === 'dropship files required') {
      for (const kind of this.dropshipFileKinds) {
        this.dropshipFiles[kind] = {id: 0, name: '', uploading: false, uploadingName: '', error: 'Завантажте файл ще раз.'};
      }
      // Інакше вибір того самого файлу не дасть change
      this.dropshipFileInputs?.forEach(ref => ref.nativeElement.value = '');
      this.labelParse = null;
      this.placesCheck = null;
    }

    // Картка #868: бек порахував місця по кошику на момент оформлення - показуємо блок з його цифрами
    if (data.error === 'dropship places mismatch' && data.data) {
      this.placesCheck = {label: Number(data.data.label), calc: Number(data.data.calc), ok: false};
    }

    if (data.error === 'wrong dropship ttn') {
      this.manualTtnError = DROPSHIP_TTN_TOO_LONG;
    }
  }
}
